import { db, auth, functions } from "../config/firebase";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  setDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  collectionGroup,
  Timestamp,
  getCountFromServer,
  increment,
} from "firebase/firestore";
import {
  sendPasswordResetEmail,
  updatePassword as updateAuthPassword,
} from "firebase/auth";

// =========================================================
// == BÚSSOLA DINÂMICA (Auditoria de Ambiente em Tempo Real)
// =========================================================
// Garante o roteamento correto mesmo se a variável .env se perder no ciclo do React
const getCurrentEnvironment = () => {
  const envVar = process.env.REACT_APP_FIREBASE_DATABASE_ID;
  if (envVar) return envVar;
  
  // Fallback: Auditoria pela URL
  const host = window?.location?.hostname || "";
  if (host.includes("homolog") || host.includes("localhost")) return "homolog";
  return "(default)";
};

// =========================================================
// == GESTÃO DE PROFISSIONAIS
// =========================================================

/**
 * @param {string} companyId
 * @param {Object} professionalData
 */
export const addProfessional = async (companyId, professionalData) => {
  const professionalsRef = collection(
    db,
    "companies",
    companyId,
    "professionals"
  );
  await addDoc(professionalsRef, {
    ...professionalData,
    active: true, // Gatekeeper: Todo profissional nasce ativo
    createdAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 */
export const getProfessionalsByCompanyId = async (companyId) => {
  const professionalsRef = collection(
    db,
    "companies",
    companyId,
    "professionals"
  );
  const q = query(professionalsRef, orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  const professionals = [];
  querySnapshot.forEach((doc) => {
    professionals.push({ id: doc.id, ...doc.data() });
  });
  return professionals;
};

/**
 * @param {string} companyId
 * @param {string} professionalId
 * @param {Object} updatedData
 */
export const updateProfessional = async (
  companyId,
  professionalId,
  updatedData
) => {
  const professionalRef = doc(
    db,
    "companies",
    companyId,
    "professionals",
    professionalId
  );
  await updateDoc(professionalRef, {
    ...updatedData,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Gatekeeper: Função para Inativar/Reativar (Soft Delete)
 * @param {string} companyId
 * @param {string} professionalId
 * @param {boolean} newStatus
 */
export const toggleProfessionalStatus = async (
  companyId,
  professionalId,
  newStatus
) => {
  try {
    const professionalRef = doc(
      db,
      "companies",
      companyId,
      "professionals",
      professionalId
    );
    await updateDoc(professionalRef, {
      active: newStatus,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Erro ao alterar status do profissional:", error);
    throw error;
  }
};

/**
 * @param {string} companyId
 * @param {string} professionalId
 */
export const deleteProfessional = async (companyId, professionalId) => {
  const professionalRef = doc(
    db,
    "companies",
    companyId,
    "professionals",
    professionalId
  );
  await deleteDoc(professionalRef);
};

// =========================================================
// == GESTÃO DE SERVIÇOS
// =========================================================

/**
 * @param {string} companyId
 * @param {Object} serviceData
 */
export const addService = async (companyId, serviceData) => {
  const servicesRef = collection(db, "companies", companyId, "services");
  await addDoc(servicesRef, {
    ...serviceData,
    price: Number(serviceData.price) || 0,
    createdAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 */
export const getServicesByCompanyId = async (companyId) => {
  const servicesRef = collection(db, "companies", companyId, "services");
  const q = query(servicesRef, orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  const services = [];
  querySnapshot.forEach((doc) => {
    services.push({ id: doc.id, ...doc.data() });
  });
  return services;
};

/**
 * @param {string} companyId
 * @param {string} serviceId
 * @param {Object} updatedData
 */
export const updateService = async (companyId, serviceId, updatedData) => {
  const serviceRef = doc(db, "companies", companyId, "services", serviceId);
  await updateDoc(serviceRef, {
    ...updatedData,
    price: Number(updatedData.price) || 0,
    updatedAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 * @param {string} serviceId
 */
export const deleteService = async (companyId, serviceId) => {
  const serviceRef = doc(db, "companies", companyId, "services", serviceId);
  await deleteDoc(serviceRef);
};

// =========================================================
// == GESTÃO DE CLIENTES
// =========================================================

/**
 * @param {string} companyId
 * @param {Object} clientData
 */
export const addClient = async (companyId, clientData) => {
  const clientsRef = collection(db, "companies", companyId, "clients");
  await addDoc(clientsRef, {
    ...clientData,
    totalAppointments: 0,
    createdAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 * @param {Array<Object>} clients
 */
export const batchAddClients = async (companyId, clients) => {
  if (!companyId || !clients || clients.length === 0) return 0;

  const batch = writeBatch(db);
  const collectionRef = collection(db, "companies", companyId, "clients");
  let count = 0;

  clients.forEach((client) => {
    const docRef = doc(collectionRef);
    const newClient = {
      name: client.name || "Cliente Sem Nome",
      phone: client.phone || "",
      email: client.email || "",
      notes: client.notes || "Importado via CSV",
      createdAt: serverTimestamp(),
      totalAppointments: 0,
      totalSpent: 0,
      lastVisit: null,
    };
    batch.set(docRef, newClient);
    count++;
  });

  try {
    await batch.commit();
    return count;
  } catch (error) {
    console.error("Erro no Batch Import:", error);
    throw error;
  }
};

/**
 * @param {string} companyId
 */
export const getClientsByCompanyId = async (companyId) => {
  const clientsRef = collection(db, "companies", companyId, "clients");
  const q = query(clientsRef, orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  const clients = [];
  querySnapshot.forEach((doc) => {
    clients.push({ id: doc.id, ...doc.data() });
  });
  return clients;
};

/**
 * @param {string} companyId
 * @param {string} clientId
 * @param {Object} updatedData
 */
export const updateClient = async (companyId, clientId, updatedData) => {
  const clientRef = doc(db, "companies", companyId, "clients", clientId);
  await updateDoc(clientRef, {
    ...updatedData,
    updatedAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 * @param {string} clientId
 */
export const deleteClient = async (companyId, clientId) => {
  const clientRef = doc(db, "companies", companyId, "clients", clientId);
  await deleteDoc(clientRef);
};

/**
 * @param {string} companyId
 * @param {string} clientId
 */
export const getAppointmentsByClientId = async (companyId, clientId) => {
  const appointmentsRef = collection(
    db,
    "companies",
    companyId,
    "appointments"
  );
  const q = query(
    appointmentsRef,
    where("clientId", "==", clientId),
    orderBy("appointmentTime", "desc")
  );
  const querySnapshot = await getDocs(q);
  const appointments = [];
  querySnapshot.forEach((doc) => {
    appointments.push({ id: doc.id, ...doc.data() });
  });
  return appointments;
};

// =========================================================
// == GESTÃO DE ESTOQUE
// =========================================================

/**
 * @param {string} companyId
 * @param {string} itemId
 * @param {number} amount
 * @param {string} reason
 * @param {string} [operatorName]
 */
export const adjustInventoryQuantity = async (
  companyId,
  itemId,
  amount,
  reason,
  operatorName
) => {
  const itemRef = doc(db, "companies", companyId, "inventory", itemId);
  const historyRef = collection(itemRef, "history");
  const batch = writeBatch(db);
  batch.update(itemRef, {
    quantity: increment(amount),
  });
  const historyLog = {
    amount: amount,
    reason: reason,
    operator: operatorName || "Sistema",
    timestamp: serverTimestamp(),
  };
  batch.set(doc(historyRef), historyLog);
  await batch.commit();
};

/**
 * @param {string} companyId
 * @param {string} itemId
 */
export const getInventoryHistoryByItemId = async (companyId, itemId) => {
  const historyRef = collection(
    db,
    "companies",
    companyId,
    "inventory",
    itemId,
    "history"
  );
  const q = query(historyRef, orderBy("timestamp", "desc"));
  const querySnapshot = await getDocs(q);
  const history = [];
  querySnapshot.forEach((doc) => {
    history.push({ id: doc.id, ...doc.data() });
  });
  return history;
};

/**
 * @param {string} companyId
 * @param {Object} itemData
 * @param {string} [operatorName]
 */
export const addInventoryItem = async (companyId, itemData, operatorName) => {
  const inventoryRef = collection(db, "companies", companyId, "inventory");
  const batch = writeBatch(db);
  const itemRef = doc(inventoryRef);
  batch.set(itemRef, {
    ...itemData,
    price: Number(itemData.price) || 0,
    quantity: Number(itemData.quantity) || 0,
    createdAt: serverTimestamp(),
  });
  const historyRef = collection(itemRef, "history");
  batch.set(doc(historyRef), {
    amount: Number(itemData.quantity) || 0,
    reason: "Criação de item",
    operator: operatorName || "Sistema",
    timestamp: serverTimestamp(),
  });
  await batch.commit();
};

/**
 * @param {string} companyId
 */
export const getInventoryItemsByCompanyId = async (companyId) => {
  const inventoryRef = collection(db, "companies", companyId, "inventory");
  const q = query(inventoryRef, orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  const items = [];
  querySnapshot.forEach((doc) => {
    items.push({ id: doc.id, ...doc.data() });
  });
  return items;
};

/**
 * @param {string} companyId
 * @param {string} itemId
 * @param {Object} updatedData
 */
export const updateInventoryItem = async (companyId, itemId, updatedData) => {
  const itemRef = doc(db, "companies", companyId, "inventory", itemId);
  const { quantity, ...safeUpdatedData } = updatedData;
  await updateDoc(itemRef, {
    ...safeUpdatedData,
    price: Number(safeUpdatedData.price) || 0,
    updatedAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 * @param {string} itemId
 */
export const deleteInventoryItem = async (companyId, itemId) => {
  const itemRef = doc(db, "companies", companyId, "inventory", itemId);
  await deleteDoc(itemRef);
};

// =========================================================
// == GESTÃO DE AGENDAMENTOS
// =========================================================

/**
 * @param {string} companyId
 * @param {Object} appointmentData
 */
export const addAppointment = async (companyId, appointmentData) => {
  const appointmentsRef = collection(
    db,
    "companies",
    companyId,
    "appointments"
  );
  const { appointmentTime, ...rest } = appointmentData;
  const firestoreTimestamp = Timestamp.fromDate(appointmentTime);
  await addDoc(appointmentsRef, {
    ...rest,
    appointmentTime: firestoreTimestamp,
    createdAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 */
export const getAppointmentsByCompanyId = async (companyId) => {
  const appointmentsRef = collection(
    db,
    "companies",
    companyId,
    "appointments"
  );
  const q = query(appointmentsRef, orderBy("appointmentTime", "asc"));
  const querySnapshot = await getDocs(q);
  const appointments = [];
  querySnapshot.forEach((doc) => {
    appointments.push({ id: doc.id, ...doc.data() });
  });
  return appointments;
};

/**
 * @param {string} companyId
 * @param {string} appointmentId
 * @param {Object} updatedData
 */
export const updateAppointment = async (
  companyId,
  appointmentId,
  updatedData
) => {
  const appointmentRef = doc(
    db,
    "companies",
    companyId,
    "appointments",
    appointmentId
  );
  const payload = {
    ...updatedData,
    updatedAt: serverTimestamp(),
  };
  if (updatedData.appointmentTime instanceof Date) {
    payload.appointmentTime = Timestamp.fromDate(updatedData.appointmentTime);
  }
  await updateDoc(appointmentRef, payload);
};

/**
 * @param {string} companyId
 * @param {string} appointmentId
 */
export const deleteAppointment = async (companyId, appointmentId) => {
  const appointmentRef = doc(
    db,
    "companies",
    companyId,
    "appointments",
    appointmentId
  );
  await deleteDoc(appointmentRef);
};

/**
 * @param {string} companyId
 * @param {string} appointmentId
 * @param {string} newStatus
 */
export const updateAppointmentStatus = async (
  companyId,
  appointmentId,
  newStatus
) => {
  const appointmentRef = doc(
    db,
    "companies",
    companyId,
    "appointments",
    appointmentId
  );
  const payload = {
    status: newStatus,
    updatedAt: serverTimestamp(),
  };
  await updateDoc(appointmentRef, payload);
};

/**
 * @param {string} companyId
 * @param {string} appointmentId
 * @param {string} type
 */
export const markAppointmentMessageAsSent = async (
  companyId,
  appointmentId,
  type
) => {
  const appointmentRef = doc(
    db,
    "companies",
    companyId,
    "appointments",
    appointmentId
  );
  const payload = {};
  if (type === "confirmation") payload.confirmationSent = true;
  if (type === "reminder") payload.reminderSent = true;

  await updateDoc(appointmentRef, {
    ...payload,
    updatedAt: serverTimestamp(),
  });
};

// =========================================================
// == CHECKOUT & FINANCEIRO & FIDELIDADE (BLINDAGEM DE PLANOS)
// =========================================================

/**
 * @param {string} companyId
 * @param {string} appointmentId
 * @param {Object} checkoutData
 * @param {string} [operatorName]
 */
export const finalizeAppointment = async (
  companyId,
  appointmentId,
  checkoutData,
  operatorName
) => {
  const batch = writeBatch(db);
  const appointmentRef = doc(
    db,
    "companies",
    companyId,
    "appointments",
    appointmentId
  );

  const apptSnap = await getDoc(appointmentRef);
  if (!apptSnap.exists()) {
    throw new Error("Agendamento não encontrado para finalizar.");
  }
  const appointmentData = apptSnap.data();
  const finalTimestamp = serverTimestamp();

  // 1. GATEKEEPER FINANCEIRO: Verificar limites do plano
  const companyRef = doc(db, "companies", companyId);
  const companySnap = await getDoc(companyRef);
  const companyData = companySnap.exists() ? companySnap.data() : {};
  // Se canUseFinancial for false, o usuário está no Starter ou similar
  const canUseFinancial =
    companyData?.subscription?.limits?.canUseFinancial === true;

  // 2. RAMIFICAÇÃO: LÓGICA RESTRITIVA PARA PLANO STARTER (CORRIGIDA)
  if (!canUseFinancial) {
    batch.update(appointmentRef, {
      status: "completed",
      checkout: {
        ...checkoutData,
        financialEntryType: "receipt_only", // Flag para indicar que não gerou caixa
        finalizedAt: finalTimestamp,
        operator: operatorName || "Sistema",
      },
      updatedAt: finalTimestamp,
    });

    if (appointmentData.clientId) {
      const clientRef = doc(
        db,
        "companies",
        companyId,
        "clients",
        appointmentData.clientId
      );
      batch.update(clientRef, {
        lastVisit: finalTimestamp,
        totalAppointments: increment(1),
      });
    }

    // INTERROMPE AQUI.
    await batch.commit();
    return;
  }

  // 3. RAMIFICAÇÃO: LÓGICA COMPLETA (PRO/PREMIUM)
  // --- LÓGICA DE COMISSÃO ---
  let commissionValue = 0;
  let commissionRateUsed = 0;

  if (appointmentData.professionalId) {
    try {
      const professionalRef = doc(
        db,
        "companies",
        companyId,
        "professionals",
        appointmentData.professionalId
      );
      const professionalSnap = await getDoc(professionalRef);
      if (professionalSnap.exists()) {
        const professionalData = professionalSnap.data();
        commissionRateUsed = Number(professionalData.commissionRate) || 0;

        const servicePrice = Number(checkoutData.servicePrice) || 0;
        if (commissionRateUsed > 0 && servicePrice > 0) {
          commissionValue = servicePrice * (commissionRateUsed / 100);
        }
      }
    } catch (error) {
      console.error("Erro ao calcular comissão:", error);
    }
  }
  // --------------------------

  batch.update(appointmentRef, {
    status: "completed",
    checkout: {
      ...checkoutData,
      financialEntryType: "registered", // Flag de controle para UI
      finalizedAt: finalTimestamp,
      operator: operatorName || "Sistema",
    },
    updatedAt: finalTimestamp,
  });

  if (checkoutData.addedProducts && checkoutData.addedProducts.length > 0) {
    for (const product of checkoutData.addedProducts) {
      if (product.quantity > 0) {
        const itemRef = doc(
          db,
          "companies",
          companyId,
          "inventory",
          product.id
        );
        const historyRef = collection(itemRef, "history");

        batch.update(itemRef, {
          quantity: increment(-product.quantity),
        });

        batch.set(doc(historyRef), {
          amount: -product.quantity,
          reason: `Venda (Atendimento #${appointmentId.substring(0, 5)})`,
          operator: operatorName || "Sistema",
          timestamp: finalTimestamp,
        });
      }
    }
  }

  const cashFlowRef = doc(collection(db, "companies", companyId, "cashFlow"));
  batch.set(cashFlowRef, {
    appointmentId: appointmentId,
    timestamp: finalTimestamp,
    finalizedAt: finalTimestamp,
    operator: operatorName || "Sistema",
    clientId: appointmentData.clientId,
    clientName: appointmentData.clientName,
    professionalId: appointmentData.professionalId,
    professionalName: appointmentData.professionalName,
    paymentMethod: checkoutData.paymentMethod,
    bandeiraId: checkoutData.bandeiraId || "",
    totalBruto: checkoutData.totalBruto,
    taxaAplicada: checkoutData.taxaAplicada || 0,
    valorLiquido: checkoutData.valorLiquido,
    servicePrice: checkoutData.servicePrice || 0,
    productsTotal: checkoutData.productsTotal || 0,

    commissionRate: commissionRateUsed,
    commissionValue: commissionValue,

    type: "inflow",
    description: `Atendimento: ${appointmentData.serviceName}`,
  });

  if (appointmentData.clientId) {
    const clientRef = doc(
      db,
      "companies",
      companyId,
      "clients",
      appointmentData.clientId
    );
    batch.update(clientRef, {
      lastVisit: finalTimestamp,
      totalAppointments: increment(1),
    });
  }

  await batch.commit();
};

/**
 * @param {string} companyId
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const getCompletedAppointmentsByDateRange = async (
  companyId,
  startDate,
  endDate
) => {
  const appointmentsRef = collection(
    db,
    "companies",
    companyId,
    "appointments"
  );
  const startTimestamp = Timestamp.fromDate(startDate);
  const endTimestamp = Timestamp.fromDate(endDate);
  const q = query(
    appointmentsRef,
    where("status", "==", "completed"),
    where("checkout.finalizedAt", ">=", startTimestamp),
    where("checkout.finalizedAt", "<=", endTimestamp),
    orderBy("checkout.finalizedAt", "desc")
  );
  const querySnapshot = await getDocs(q);
  const appointments = [];
  querySnapshot.forEach((doc) => {
    appointments.push({ id: doc.id, ...doc.data() });
  });
  return appointments;
};

/**
 * @param {string} companyId
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const getCashFlowByDateRange = async (companyId, startDate, endDate) => {
  const cashFlowRef = collection(db, "companies", companyId, "cashFlow");
  const startTimestamp = Timestamp.fromDate(startDate);
  const endTimestamp = Timestamp.fromDate(endDate);

  const q = query(
    cashFlowRef,
    where("finalizedAt", ">=", startTimestamp),
    where("finalizedAt", "<=", endTimestamp),
    orderBy("finalizedAt", "desc")
  );

  const querySnapshot = await getDocs(q);
  const entries = [];
  querySnapshot.forEach((doc) => {
    entries.push({ id: doc.id, ...doc.data() });
  });
  return entries;
};

/**
 * @param {string} companyId
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const getRevenueByDateRange = async (companyId, startDate, endDate) => {
  const cashFlowRef = collection(db, "companies", companyId, "cashFlow");
  const startTimestamp = Timestamp.fromDate(startDate);
  const endTimestamp = Timestamp.fromDate(endDate);

  const q = query(
    cashFlowRef,
    where("type", "==", "inflow"),
    where("finalizedAt", ">=", startTimestamp),
    where("finalizedAt", "<=", endTimestamp)
  );

  const querySnapshot = await getDocs(q);
  let totalRevenue = 0;

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    if (data.valorLiquido) {
      totalRevenue += data.valorLiquido;
    } else if (data.totalBruto) {
      totalRevenue += data.totalBruto;
    }
  });

  return totalRevenue;
};

/**
 * @param {string} companyId
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {string} status
 * @param {string} dateField
 */
export const getAppointmentCountByStatus = async (
  companyId,
  startDate,
  endDate,
  status,
  dateField
) => {
  try {
    const appointmentsRef = collection(
      db,
      "companies",
      companyId,
      "appointments"
    );
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    const q = query(
      appointmentsRef,
      where("status", "==", "status"),
      where(dateField, ">=", startTimestamp),
      where(dateField, "<=", endTimestamp)
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error("Erro em getAppointmentCountByStatus:", error);
    throw new Error(
      `Falha ao buscar contagem de status '${status}'. Verifique os índices do Firestore.`
    );
  }
};

/**
 * @param {string} companyId
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const getAppointmentsByDate = async (companyId, startDate, endDate) => {
  try {
    const appointmentsRef = collection(
      db,
      "companies",
      companyId,
      "appointments"
    );
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const q = query(
      appointmentsRef,
      where("appointmentTime", ">=", startTimestamp),
      where("appointmentTime", "<=", endTimestamp)
    );

    const querySnapshot = await getDocs(q);
    const appointments = [];
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    return appointments;
  } catch (error) {
    console.error("Erro em getAppointmentsByDate:", error);
    throw new Error(
      `Falha ao buscar agendamentos do dia. Verifique os índices do Firestore.`
    );
  }
};

/**
 * @param {string} companyId
 */
export const getActiveProfessionalsForCalendar = async (companyId) => {
  const professionalsRef = collection(
    db,
    "companies",
    companyId,
    "professionals"
  );
  const q = query(
    professionalsRef,
    where("active", "==", true),
    orderBy("name", "asc")
  );

  const querySnapshot = await getDocs(q);
  const professionals = [];
  querySnapshot.forEach((doc) => {
    professionals.push({ id: doc.id, ...doc.data() });
  });
  return professionals;
};

/**
 * @param {string} companyId
 */
export const getAllCompletedAppointments = async (companyId) => {
  try {
    const appointmentsRef = collection(
      db,
      "companies",
      companyId,
      "appointments"
    );
    const q = query(
      appointmentsRef,
      where("status", "==", "completed"),
      orderBy("checkout.finalizedAt", "desc")
    );
    const querySnapshot = await getDocs(q);
    const appointments = [];
    querySnapshot.forEach((doc) => {
      appointments.push({ id: doc.id, ...doc.data() });
    });
    return appointments;
  } catch (error) {
    console.error("Erro em getAllCompletedAppointments:", error);
    throw new Error(
      `Falha ao buscar histórico de agendamentos. Verifique os índices do Firestore.`
    );
  }
};

// =========================================================
// == GESTÃO DE BLOQUEIOS DE AGENDA (EXCEÇÕES/FERIADOS)
// =========================================================

/**
 * @param {string} companyId
 * @param {Object} blockData
 */
export const addBlockedSlot = async (companyId, blockData) => {
  const blocksRef = collection(db, "companies", companyId, "blockedSlots");
  await addDoc(blocksRef, {
    ...blockData,
    createdAt: serverTimestamp(),
  });
};

/**
 * @param {string} companyId
 */
export const getBlockedSlots = async (companyId) => {
  const blocksRef = collection(db, "companies", companyId, "blockedSlots");
  const q = query(blocksRef, orderBy("date", "asc"));

  const querySnapshot = await getDocs(q);
  const blocks = [];
  querySnapshot.forEach((doc) => {
    blocks.push({ id: doc.id, ...doc.data() });
  });
  return blocks;
};

/**
 * @param {string} companyId
 * @param {string} professionalId
 */
export const getProfessionalBlockedSlots = async (
  companyId,
  professionalId
) => {
  const blocksRef = collection(db, "companies", companyId, "blockedSlots");
  const q = query(
    blocksRef,
    where("professionalId", "==", professionalId),
    orderBy("date", "asc")
  );

  const querySnapshot = await getDocs(q);
  const blocks = [];
  querySnapshot.forEach((doc) => {
    blocks.push({ id: doc.id, ...doc.data() });
  });
  return blocks;
};

/**
 * @param {string} companyId
 * @param {string} blockId
 */
export const deleteBlockedSlot = async (companyId, blockId) => {
  const blockRef = doc(db, "companies", companyId, "blockedSlots", blockId);
  await deleteDoc(blockRef);
};

// =========================================================
// == SUPER ADMIN E GESTÃO DE EMPRESAS (ATUALIZADO V2)
// =========================================================

/**
 * @param {Object} companyData
 * @param {Object} adminData
 */
export const createCompanyWithDirectAdmin = async (companyData, adminData) => {
  let planData = null;
  if (companyData.planId) {
    const planRef = doc(db, "plans", companyData.planId);
    const planSnap = await getDoc(planRef);
    if (planSnap.exists()) {
      planData = planSnap.data();
    }
  }

  let subscriptionStatus = "active";
  let trialExpiresAt = null;

  if (planData && planData.trialDurationDays > 0) {
    const now = new Date();
    const trialExpirationDate = new Date(
      now.setDate(now.getDate() + Number(planData.trialDurationDays))
    );
    trialExpiresAt = Timestamp.fromDate(trialExpirationDate);
    subscriptionStatus = "trial";
  }

  const subscriptionData = {
    planId: companyData.planId || "starter",
    planName: planData ? planData.name : "Starter",
    status: subscriptionStatus,
    limits: planData ? planData.limits || {} : {},
    trialExpiresAt: trialExpiresAt,
    startedAt: serverTimestamp(),
  };

  const batch = writeBatch(db);
  const companyRef = doc(collection(db, "companies"));
  const companyId = companyRef.id;

  const newCompany = {
    name: companyData.name,
    cnpj: companyData.document, 
    planId: companyData.planId,
    createdAt: serverTimestamp(),
    status: "active",
    active: true,
    memberUids: [adminData.uid], 
    ownerUid: adminData.uid,
    adminUid: adminData.uid,
    invitationStatus: "accepted", 
    subscription: subscriptionData,
    subscriptionStatus: subscriptionStatus,
    contact: {
      name: adminData.name,
      email: adminData.email,
    },
  };

  const userProfileRef = doc(db, "userProfiles", adminData.uid);
  const userProfileData = {
    email: adminData.email,
    name: adminData.name,
    role: "admin",
    companyId: companyId,
    createdAt: serverTimestamp(),
  };

  const companyUserRef = doc(db, `companies/${companyId}/users`, adminData.uid);
  const companyUserData = {
    email: adminData.email,
    name: adminData.name,
    role: "admin",
    joinedAt: serverTimestamp(),
    status: "active",
  };

  batch.set(companyRef, newCompany);
  batch.set(userProfileRef, userProfileData);
  batch.set(companyUserRef, companyUserData);

  try {
    await batch.commit();
    console.log("✅ Empresa e Perfil Admin criados com sucesso via Batch!");

    try {
      await sendPasswordResetEmail(auth, adminData.email);
      console.log(`📧 Email de definição de senha enviado para ${adminData.email}`);
    } catch (emailError) {
      console.warn("⚠️ Empresa criada, mas falha ao enviar email de senha:", emailError);
    }

    return companyId;
  } catch (error) {
    console.error("❌ Erro ao criar empresa com admin direto:", error);
    throw error;
  }
};

/**
 * @param {Object} companyData
 */
export const createCompanyAndInviteAdmin = async (companyData) => {
  try {
    if (!companyData.planId) {
      throw new Error("Nenhum plano foi selecionado para a nova empresa.");
    }

    let planData = companyData.selectedPlanData;

    if (!planData) {
      const planRef = doc(db, "plans", companyData.planId);
      const planSnap = await getDoc(planRef);
      if (!planSnap.exists()) {
        throw new Error(
          "O plano selecionado é inválido ou não foi encontrado."
        );
      }
      planData = planSnap.data();
    }

    let trialExpiresAt = null;
    let trialDurationDays = Number(planData.trialDurationDays) || 0;
    let subscriptionStatus = "active";

    if (trialDurationDays > 0) {
      const now = new Date();
      const trialExpirationDate = new Date(
        now.setDate(now.getDate() + trialDurationDays)
      );
      trialExpiresAt = Timestamp.fromDate(trialExpirationDate);
      subscriptionStatus = "trial";
    }

    const subscriptionData = {
      planId: companyData.planId,
      planName: planData.name,
      status: subscriptionStatus,
      limits: planData.limits || {},
      trialExpiresAt: trialExpiresAt,
      startedAt: serverTimestamp(),
    };

    const batch = writeBatch(db);
    const companyRef = doc(collection(db, "companies"));

    const companyPayload = {
      name: companyData.companyName,
      cnpj: companyData.cnpj,
      contact: {
        name: companyData.contactName,
        email: companyData.adminEmail,
        phone: companyData.phone,
      },
      address: companyData.address || "",
      createdAt: serverTimestamp(),
      active: true,
      invitationStatus: "pending",
      adminUid: null,
      memberUids: [],
      subscription: subscriptionData,
      planId: companyData.planId,
      subscriptionStatus: subscriptionStatus,
    };

    batch.set(companyRef, companyPayload);

    const inviteRef = doc(
      collection(companyRef, "invites"),
      companyData.adminEmail.toLowerCase()
    );
    const invitePayload = {
      companyId: companyRef.id,
      companyName: companyData.companyName,
      email: companyData.adminEmail,
      name: companyData.contactName,
      role: "admin",
      invitedAt: serverTimestamp(),
    };
    batch.set(inviteRef, invitePayload);

    await batch.commit();
    await sendPasswordResetEmail(auth, companyData.adminEmail);
    return companyRef;
  } catch (error) {
    console.error("Erro detalhado:", error.code, error.message);
    if (error.code === "auth/user-not-found") {
      throw new Error(
        "Usuário não encontrado. Crie-o no painel do Firebase Authentication primeiro."
      );
    }
    throw new Error(
      error.message || "Ocorreu um erro ao processar a solicitação."
    );
  }
};

export const getAllCompanies = async () => {
  const querySnapshot = await getDocs(collection(db, "companies"));
  const companies = [];
  querySnapshot.forEach((doc) => {
    companies.push({ id: doc.id, ...doc.data() });
  });
  return companies;
};

/**
 * @param {string} companyId
 * @param {boolean} newStatus
 */
export const updateCompanyStatus = async (companyId, newStatus) => {
  const companyRef = doc(db, "companies", companyId);
  await updateDoc(companyRef, { active: newStatus });
};

/**
 * @param {string} companyId
 */
export const getCompanyById = async (companyId) => {
  const companyRef = doc(db, "companies", companyId);
  const docSnap = await getDoc(companyRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    if (data.createdAt && typeof data.createdAt.toDate === "function") {
      try {
        data.createdAtFormatted = data.createdAt
          .toDate()
          .toLocaleDateString("pt-BR");
      } catch (e) {
        console.error("Erro ao formatar createdAt:", e);
        data.createdAtFormatted = "Data inválida";
      }
    } else {
      data.createdAtFormatted = "Não informada";
    }
    return { id: docSnap.id, ...data };
  } else {
    throw new Error("Nenhuma empresa encontrada com este ID.");
  }
};

// =========================================================
// == ATUALIZAÇÃO DE EMPRESA (COM LÓGICA DE PLANOS V2)
// =========================================================

/**
 * @param {string} companyId
 * @param {Object} updatedData
 */
export const updateCompanyDetails = async (companyId, updatedData) => {
  const companyRef = doc(db, "companies", companyId);

  const payload = {};

  if (updatedData.name !== undefined) payload.name = updatedData.name;
  if (updatedData.cnpj !== undefined) payload.cnpj = updatedData.cnpj;
  if (updatedData.address !== undefined) payload.address = updatedData.address;

  if (updatedData.contactName !== undefined)
    payload["contact.name"] = updatedData.contactName;
  if (updatedData.contactEmail !== undefined)
    payload["contact.email"] = updatedData.contactEmail;
  if (updatedData.contactPhone !== undefined)
    payload["contact.phone"] = updatedData.contactPhone;

  if (updatedData.planId !== undefined) {
    payload.planId = updatedData.planId;
    payload["subscription.planId"] = updatedData.planId;

    try {
      const planRef = doc(db, "plans", updatedData.planId);
      const planSnap = await getDoc(planRef);

      if (planSnap.exists()) {
        const planData = planSnap.data();
        payload["subscription.limits"] = planData.limits || {};
        payload["subscription.planName"] = planData.name;
      }
    } catch (err) {
      console.error("Erro ao atualizar limites do novo plano:", err);
    }
  }

  if (updatedData.subscriptionStatus !== undefined) {
    payload.subscriptionStatus = updatedData.subscriptionStatus;
    payload["subscription.status"] = updatedData.subscriptionStatus;
  }

  if (updatedData.receiptFooter !== undefined)
    payload.receiptFooter = updatedData.receiptFooter;
  if (updatedData.logoUrl !== undefined) payload.logoUrl = updatedData.logoUrl;
  if (updatedData.settingsCompleted !== undefined)
    payload.settingsCompleted = updatedData.settingsCompleted;

  if (updatedData.settings !== undefined)
    payload.settings = updatedData.settings;

  if (Object.keys(payload).length === 0) return;

  await updateDoc(companyRef, payload);
};

/**
 * @param {string} companyId
 */
export const deleteCompany = async (companyId) => {
  const companyRef = doc(db, "companies", companyId);
  await deleteDoc(companyRef);
};

/**
 * @param {string} companyId
 * @param {string} newStatus
 */
export const updateInvitationStatus = async (companyId, newStatus) => {
  const companyRef = doc(db, "companies", companyId);
  await updateDoc(companyRef, { invitationStatus: newStatus });
};

/**
 * @param {Object} company
 */
export const resendInvite = async (company) => {
  if (!company || !company.id || !company.contact) {
    throw new Error("Dados da empresa inválidos para reenviar o convite.");
  }
  const companyRef = doc(db, "companies", company.id);
  const inviteRef = doc(
    collection(companyRef, "invites"),
    company.contact.email.toLowerCase()
  );
  const invitePayload = {
    companyId: company.id,
    companyName: company.name,
    email: company.contact.email,
    name: company.contact.name,
    role: "admin",
    invitedAt: serverTimestamp(),
  };
  await setDoc(inviteRef, invitePayload);
  await updateDoc(companyRef, { invitationStatus: "pending" });
  await sendPasswordResetEmail(auth, company.contact.email);
};

/**
 * @param {string} newPassword
 */
export const updateUserPassword = async (newPassword) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Nenhum usuário autenticado para atualizar a senha.");
  }
  await updateAuthPassword(user, newPassword);
  const userProfileRef = doc(db, "userProfiles", user.uid);
  await updateDoc(userProfileRef, {
    forcePasswordChange: false,
  });
};

/**
 * @param {string} companyId
 * @param {string} companyName
 * @param {Object} newUserData
 */
export const inviteUserToCompany = async (
  companyId,
  companyName,
  newUserData
) => {
  const invitesRef = collection(db, "companies", companyId, "invites");
  const inviteDoc = doc(invitesRef, newUserData.email.toLowerCase());
  const docSnap = await getDoc(inviteDoc);
  if (docSnap.exists()) {
    throw new Error("Já existe um convite pendente para este e-mail.");
  }
  await setDoc(inviteDoc, {
    companyId,
    companyName,
    ...newUserData,
    invitedAt: serverTimestamp(),
  });
  await sendPasswordResetEmail(auth, newUserData.email);
};

/**
 * @param {Object} user
 */
export const acceptInvite = async (user) => {
  const invitesCollectionGroup = collectionGroup(db, "invites");
  const q = query(
    invitesCollectionGroup,
    where("email", "==", user.email.toLowerCase())
  );
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) {
    console.log("Nenhum convite encontrado para:", user.email);
    return null;
  }
  const inviteDoc = querySnapshot.docs[0];
  const inviteData = inviteDoc.data();
  const companyRef = doc(db, "companies", inviteData.companyId);
  const userInCompanyRef = doc(collection(companyRef, "users"), user.uid);
  const userProfileRef = doc(db, "userProfiles", user.uid);
  const batch = writeBatch(db);
  batch.set(userInCompanyRef, {
    email: user.email,
    name: inviteData.name,
    role: inviteData.role,
    joinedAt: serverTimestamp(),
  });
  const companyUpdatePayload = {
    memberUids: arrayUnion(user.uid),
  };
  if (inviteData.role === "admin") {
    companyUpdatePayload.invitationStatus = "accepted";
    companyUpdatePayload.adminUid = user.uid;
  }
  batch.update(companyRef, companyUpdatePayload);
  batch.set(userProfileRef, {
    companyId: inviteData.companyId,
    role: inviteData.role,
    name: inviteData.name,
    email: user.email,
  });
  batch.delete(inviteDoc.ref);
  await batch.commit();
  return inviteData.companyId;
};

/**
 * @param {string} companyId
 */
export const getUsersByCompanyId = async (companyId) => {
  const usersRef = collection(db, "companies", companyId, "users");
  const q = query(usersRef, orderBy("name", "asc"));
  const querySnapshot = await getDocs(q);
  const users = [];
  querySnapshot.forEach((doc) => {
    users.push({ id: doc.id, ...doc.data() });
  });
  return users;
};

/**
 * @param {string} companyId
 * @param {string} userId
 * @param {string} newRole
 */
export const updateUserRole = async (companyId, userId, newRole) => {
  const userRef = doc(db, "companies", companyId, "users", userId);
  const profileRef = doc(db, "userProfiles", userId);
  const batch = writeBatch(db);
  batch.update(userRef, { role: newRole });
  batch.update(profileRef, { role: newRole });
  await batch.commit();
};

/**
 * @param {string} companyId
 * @param {string} userId
 */
export const removeUserFromCompany = async (companyId, userId) => {
  const companyRef = doc(db, "companies", companyId);
  const userInCompanyRef = doc(companyRef, "users", userId);
  const userProfileRef = doc(db, "userProfiles", userId);
  const batch = writeBatch(db);
  batch.update(companyRef, { memberUids: arrayRemove(userId) });
  batch.delete(userInCompanyRef);
  batch.delete(userProfileRef);
  await batch.commit();
};

/**
 * @param {string} companyId
 */
export const getUsersCountByCompanyId = async (companyId) => {
  if (!companyId) {
    console.error("getUsersCountByCompanyId: companyId não fornecido.");
    return 0;
  }
  try {
    const usersCollectionRef = collection(db, "companies", companyId, "users");
    const snapshot = await getCountFromServer(usersCollectionRef);
    return snapshot.data().count;
  } catch (error) {
    console.error("Erro ao contar usuários:", error);
    return 0;
  }
};

/**
 * @param {Object} subUserData
 */
export const createSubUserAccount = async (subUserData) => {
  const createSubUser = httpsCallable(functions, "createSubUser");
  try {
    // 🟢 BÚSSOLA: Disparando o ambiente auditado
    const payload = {
      ...subUserData,
      environment: getCurrentEnvironment(),
      clientBaseUrl: window?.location?.origin || ""
    };
    
    const result = await createSubUser(payload);
    return result.data;
  } catch (error) {
    if (error.code === "functions/already-exists") {
      throw new Error("Este nome de usuário já está em uso.");
    }
    console.error("Erro ao chamar Cloud Function:", error);
    throw new Error(error.message || "Ocorreu um erro desconhecido.");
  }
};

/**
 * @param {string} companyId
 * @param {string} subUserId
 */
export const deleteSubUser = async (companyId, subUserId) => {
  const deleteSubUserFunc = httpsCallable(functions, "deleteSubUser");
  try {
    // 🟢 BÚSSOLA: Disparando o ambiente auditado
    const payload = {
      companyId,
      subUserId,
      environment: getCurrentEnvironment(),
      clientBaseUrl: window?.location?.origin || ""
    };
    
    const result = await deleteSubUserFunc(payload);
    return result.data;
  } catch (error) {
    console.error("Erro ao chamar Cloud Function de deleção:", error);
    throw new Error(
      error.message || "Ocorreu um erro desconhecido ao deletar o sub-usuário."
    );
  }
};

/**
 * @param {string} userId
 * @param {Object} layout
 */
export const saveUserDashboardLayout = async (userId, layout) => {
  if (!userId) throw new Error("ID do usuário inválido.");
  try {
    const userSettingsRef = doc(db, "userSettings", userId);
    await setDoc(userSettingsRef, { dashboardLayout: layout }, { merge: true });
  } catch (error) {
    console.error("Erro ao salvar layout:", error);
    throw new Error("Não foi possível salvar seu layout.");
  }
};

/**
 * @param {string} userId
 */
export const getUserDashboardLayout = async (userId) => {
  if (!userId) return null;
  try {
    const userSettingsRef = doc(db, "userSettings", userId);
    const docSnap = await getDoc(userSettingsRef);
    if (docSnap.exists() && docSnap.data().dashboardLayout) {
      return docSnap.data().dashboardLayout;
    }
    return null;
  } catch (error) {
    console.error("Erro ao buscar layout:", error);
    throw new Error("Não foi possível carregar seu layout.");
  }
};

// =========================================================
// == GESTÃO DE PLANOS (CRUD SUPER ADMIN - ATUALIZADO COM VISIBILIDADE HIBRIDA)
// =========================================================

/**
 * @param {Object} planData
 */
export const addSubscriptionPlan = async (planData) => {
  const plansCollectionRef = collection(db, "plans");
  const payload = {
    ...planData,
    price: Number(planData.price) || 0,
    trialDurationDays: Number(planData.trialDurationDays) || 0,
    isVisible: planData.isVisible !== undefined ? planData.isVisible : true,
    createdAt: serverTimestamp(),
  };
  await addDoc(plansCollectionRef, payload);
};

/**
 * @param {boolean} [onlyVisible=false]
 */
export const getSubscriptionPlans = async (onlyVisible = false) => {
  const plansCollectionRef = collection(db, "plans");
  const q = query(plansCollectionRef, orderBy("price", "asc"));
  const querySnapshot = await getDocs(q);
  let plans = [];
  querySnapshot.forEach((doc) => {
    plans.push({ id: doc.id, ...doc.data() });
  });
  if (onlyVisible) {
    plans = plans.filter((plan) => plan.isVisible !== false);
  }
  return plans;
};

/**
 * @param {string} planId
 * @param {Object} planData
 */
export const updateSubscriptionPlan = async (planId, planData) => {
  const planRef = doc(db, "plans", planId);
  const payload = {
    ...planData,
    updatedAt: serverTimestamp(),
  };
  await updateDoc(planRef, payload);
};

/**
 * @param {string} planId
 */
export const deleteSubscriptionPlan = async (planId) => {
  const planRef = doc(db, "plans", planId);
  await deleteDoc(planRef);
};

// =========================================================
// == ALERTA DE ESTOQUE (NOVO FASE 11.8)
// =========================================================

/**
 * @param {string} companyId
 * @param {number} [limitCount=5]
 */
export const getLowStockItems = async (companyId, limitCount = 5) => {
  try {
    const inventoryRef = collection(db, "companies", companyId, "inventory");
    const q = query(
      inventoryRef,
      where("quantity", "<=", limitCount),
      orderBy("quantity", "asc")
    );
    const querySnapshot = await getDocs(q);
    const items = [];
    querySnapshot.forEach((doc) => {
      items.push({ id: doc.id, ...doc.data() });
    });
    return items;
  } catch (error) {
    console.error("Erro ao buscar itens com estoque baixo:", error);
    return [];
  }
};

// =========================================================
// == GESTÃO DE DESPESAS (MIGRADO PARA SUB-COLEÇÃO)
// =========================================================

/**
 * @param {string} companyId
 * @param {Object} expenseData
 * @param {string} [operatorName]
 */
export const addExpense = async (companyId, expenseData, operatorName) => {
  if (!companyId) throw new Error("ID da empresa é obrigatório.");

  const expensesRef = collection(db, "companies", companyId, "expenses");

  await addDoc(expensesRef, {
    ...expenseData,
    amount: Number(expenseData.amount) || 0,
    date: expenseData.date
      ? Timestamp.fromDate(new Date(expenseData.date))
      : serverTimestamp(),
    createdAt: serverTimestamp(),
    createdBy: operatorName || "Sistema",
  });
};

/**
 * @param {string} companyId
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const getExpensesByDateRange = async (companyId, startDate, endDate) => {
  if (!companyId) throw new Error("ID da empresa é obrigatório.");

  const expensesRef = collection(db, "companies", companyId, "expenses");
  const startTimestamp = Timestamp.fromDate(new Date(startDate));
  const endTimestamp = Timestamp.fromDate(new Date(endDate));

  const q = query(
    expensesRef,
    where("date", ">=", startTimestamp),
    where("date", "<=", endTimestamp),
    orderBy("date", "desc")
  );

  const querySnapshot = await getDocs(q);
  const expenses = [];
  querySnapshot.forEach((doc) => {
    expenses.push({ id: doc.id, ...doc.data() });
  });
  return expenses;
};

/**
 * @param {string} companyId
 * @param {string} expenseId
 * @param {Object} updatedData
 */
export const updateExpense = async (companyId, expenseId, updatedData) => {
  const expenseRef = doc(db, "companies", companyId, "expenses", expenseId);

  const payload = { ...updatedData };

  if (payload.amount) payload.amount = Number(payload.amount);
  if (payload.date) payload.date = Timestamp.fromDate(new Date(payload.date));

  payload.updatedAt = serverTimestamp();

  await updateDoc(expenseRef, payload);
};

/**
 * @param {string} companyId
 * @param {string} expenseId
 */
export const deleteExpense = async (companyId, expenseId) => {
  const expenseRef = doc(db, "companies", companyId, "expenses", expenseId);
  await deleteDoc(expenseRef);
};

/**
 * Função de Relatório Financeiro Simplificado (Entradas - Saídas)
 * @param {string} companyId
 * @param {Date} startDate
 * @param {Date} endDate
 */
export const getFinancialSummary = async (companyId, startDate, endDate) => {
  const revenueData = await getRevenueByDateRange(
    companyId,
    startDate,
    endDate
  );

  const expensesList = await getExpensesByDateRange(
    companyId,
    startDate,
    endDate
  );

  const totalExpenses = expensesList.reduce(
    (acc, curr) => acc + (curr.amount || 0),
    0
  );

  return {
    revenue: revenueData,
    expenses: totalExpenses,
    balance: revenueData - totalExpenses,
  };
};

/**
 * @param {string} companyId
 * @param {string} phone
 */
export const checkIfClientExistsByPhone = async (companyId, phone) => {
  try {
    const clientsRef = collection(db, "companies", companyId, "clients");
    const q = query(clientsRef, where("phone", "==", phone));
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Erro ao verificar cliente:", error);
    throw error;
  }
};

/**
 * Verifica se um SLUG (link personalizado) já está em uso
 * @param {string} slug
 */
export const checkSlugAvailability = async (slug) => {
  const q = query(collection(db, "companies"), where("slug", "==", slug));
  const snapshot = await getDocs(q);
  return snapshot.empty; // Retorna true se estiver livre
};

/**
 * Busca empresa pelo SLUG
 * @param {string} slug
 */
export const getCompanyBySlug = async (slug) => {
  const q = query(collection(db, "companies"), where("slug", "==", slug));
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  // Retorna os dados + o ID real
  return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
};
